/**
 * Spending maths.
 *
 * One rule decides everything here: `spendingDeltaSen`. An expense adds, a
 * refund subtracts, income and transfers contribute nothing. A refund is stored
 * as its own positive-amount record, so the original expense is never edited
 * and history stays truthful.
 */
import { sumSen } from './money.js';
import {
  DEFAULT_TIME_ZONE,
  addDays,
  addMonths,
  dayKey,
  dayRange,
  daysInMonth,
  isWithin,
  monthKey,
  monthRange,
  weekRange,
  type InstantRange,
} from './time.js';
import type { Category, Transaction } from './schemas.js';

/** How much a record moves the "what I spent" number. */
export function spendingDeltaSen(
  transaction: Transaction,
  categories: readonly Category[] = [],
): number {
  if (transaction.deletedAt) return 0;
  const category = categories.find((entry) => entry.id === transaction.categoryId);
  if (category?.excludeFromSpending) return 0;

  switch (transaction.type) {
    case 'expense':
      return transaction.amountSen;
    case 'refund':
      return -transaction.amountSen;
    case 'income':
    case 'transfer':
      return 0;
    default:
      return 0;
  }
}

export function isSpending(transaction: Transaction, categories: readonly Category[] = []): boolean {
  return spendingDeltaSen(transaction, categories) !== 0;
}

export const activeTransactions = (transactions: readonly Transaction[]): Transaction[] =>
  transactions.filter((transaction) => !transaction.deletedAt);

export function totalSpentSen(
  transactions: readonly Transaction[],
  categories: readonly Category[] = [],
): number {
  return sumSen(transactions.map((transaction) => spendingDeltaSen(transaction, categories)));
}

export function inRange(
  transactions: readonly Transaction[],
  range: InstantRange,
): Transaction[] {
  return transactions.filter(
    (transaction) => !transaction.deletedAt && isWithin(transaction.occurredAt, range),
  );
}

export interface PeriodTotalsOptions {
  now?: Date;
  timeZone?: string;
  categories?: readonly Category[];
}

export interface PeriodTotals {
  todaySen: number;
  weekSen: number;
  monthSen: number;
  monthKey: string;
  dayKey: string;
}

/** The three headline numbers on Today. */
export function periodTotals(
  transactions: readonly Transaction[],
  options: PeriodTotalsOptions = {},
): PeriodTotals {
  const { now = new Date(), timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  const today = dayKey(now, timeZone);
  const month = monthKey(now, timeZone);
  return {
    todaySen: totalSpentSen(inRange(transactions, dayRange(today, timeZone)), categories),
    weekSen: totalSpentSen(inRange(transactions, weekRange(now, timeZone)), categories),
    monthSen: totalSpentSen(inRange(transactions, monthRange(month, timeZone)), categories),
    monthKey: month,
    dayKey: today,
  };
}

export interface BudgetStatus {
  budgetSen: number;
  spentSen: number;
  remainingSen: number;
  /** 0-100, clamped, so a bar can never overflow its track. */
  percentUsed: number;
  isOver: boolean;
  daysInMonth: number;
  dayOfMonth: number;
  daysRemaining: number;
  /** What is left to spend per remaining day, or null when there is no budget. */
  dailyAllowanceSen: number | null;
  /** Spend so far divided by days elapsed. */
  paceSen: number;
  /** Projected month-end spend at the current pace. */
  projectedSen: number;
}

export function budgetStatus(
  transactions: readonly Transaction[],
  budgetSen: number,
  options: PeriodTotalsOptions = {},
): BudgetStatus {
  const { now = new Date(), timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  const month = monthKey(now, timeZone);
  const spentSen = totalSpentSen(inRange(transactions, monthRange(month, timeZone)), categories);
  const totalDays = daysInMonth(month);
  const dayOfMonth = Number(dayKey(now, timeZone).slice(8, 10));
  const daysRemaining = Math.max(0, totalDays - dayOfMonth);
  const remainingSen = budgetSen - spentSen;

  return {
    budgetSen,
    spentSen,
    remainingSen,
    percentUsed: budgetSen > 0 ? Math.min(100, Math.round((spentSen / budgetSen) * 100)) : 0,
    isOver: budgetSen > 0 && spentSen > budgetSen,
    daysInMonth: totalDays,
    dayOfMonth,
    daysRemaining,
    dailyAllowanceSen:
      budgetSen > 0 ? Math.max(0, Math.trunc(remainingSen / Math.max(1, daysRemaining + 1))) : null,
    paceSen: Math.trunc(spentSen / Math.max(1, dayOfMonth)),
    projectedSen: Math.trunc((spentSen / Math.max(1, dayOfMonth)) * totalDays),
  };
}

export interface CategoryTotal {
  categoryId: string;
  name: string;
  totalSen: number;
  count: number;
  /** Share of the period's spending, 0-100 with one decimal. */
  share: number;
}

export function categoryBreakdown(
  transactions: readonly Transaction[],
  categories: readonly Category[],
): CategoryTotal[] {
  const totals = new Map<string, { totalSen: number; count: number }>();

  for (const transaction of transactions) {
    const delta = spendingDeltaSen(transaction, categories);
    if (delta === 0) continue;
    const entry = totals.get(transaction.categoryId) ?? { totalSen: 0, count: 0 };
    entry.totalSen += delta;
    entry.count += 1;
    totals.set(transaction.categoryId, entry);
  }

  const grand = sumSen([...totals.values()].map((entry) => entry.totalSen));

  return [...totals.entries()]
    .map(([categoryId, entry]) => ({
      categoryId,
      name: categories.find((category) => category.id === categoryId)?.name ?? 'Uncategorised',
      totalSen: entry.totalSen,
      count: entry.count,
      share: grand > 0 ? Math.round((entry.totalSen / grand) * 1000) / 10 : 0,
    }))
    .sort((a, b) => b.totalSen - a.totalSen || a.name.localeCompare(b.name));
}

export interface DailyTotal {
  dayKey: string;
  totalSen: number;
}

/** One entry per day in the month, including days with nothing spent. */
export function dailyTotals(
  transactions: readonly Transaction[],
  month: string,
  options: { timeZone?: string; categories?: readonly Category[] } = {},
): DailyTotal[] {
  const { timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  const totals = new Map<string, number>();
  for (const transaction of inRange(transactions, monthRange(month, timeZone))) {
    const key = dayKey(transaction.occurredAt, timeZone);
    totals.set(key, (totals.get(key) ?? 0) + spendingDeltaSen(transaction, categories));
  }
  const days = daysInMonth(month);
  const first = `${month}-01`;
  return Array.from({ length: days }, (_unused, index) => {
    const key = addDays(first, index);
    return { dayKey: key, totalSen: totals.get(key) ?? 0 };
  });
}

export interface MonthlyTotal {
  monthKey: string;
  totalSen: number;
  count: number;
}

/** The trailing `months` months ending with `endMonth`, oldest first. */
export function monthlyTrend(
  transactions: readonly Transaction[],
  endMonth: string,
  months = 6,
  options: { timeZone?: string; categories?: readonly Category[] } = {},
): MonthlyTotal[] {
  const { timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  return Array.from({ length: months }, (_unused, index) => {
    const key = addMonths(endMonth, index - (months - 1));
    const slice = inRange(transactions, monthRange(key, timeZone));
    return {
      monthKey: key,
      totalSen: totalSpentSen(slice, categories),
      count: slice.filter((transaction) => spendingDeltaSen(transaction, categories) > 0).length,
    };
  });
}

/** Average spend per day elapsed so far in the month (or per whole day for a past month). */
export function dailyAverageSen(
  transactions: readonly Transaction[],
  month: string,
  options: PeriodTotalsOptions = {},
): number {
  const { now = new Date(), timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  const total = totalSpentSen(inRange(transactions, monthRange(month, timeZone)), categories);
  const currentMonth = monthKey(now, timeZone);
  const divisor =
    month === currentMonth ? Number(dayKey(now, timeZone).slice(8, 10)) : daysInMonth(month);
  return Math.trunc(total / Math.max(1, divisor));
}

export function largestExpenses(
  transactions: readonly Transaction[],
  categories: readonly Category[],
  limit = 5,
): Transaction[] {
  return transactions
    .filter((transaction) => spendingDeltaSen(transaction, categories) > 0)
    .sort((a, b) => b.amountSen - a.amountSen || a.occurredAt.localeCompare(b.occurredAt))
    .slice(0, limit);
}

export interface RecurringMerchant {
  merchant: string;
  count: number;
  totalSen: number;
  averageSen: number;
  lastSeenAt: string;
  /** Median gap between visits, in days -- a hint that this is a subscription. */
  medianGapDays: number | null;
}

/** Merchants seen at least `minCount` times in the window. */
export function recurringMerchants(
  transactions: readonly Transaction[],
  categories: readonly Category[],
  minCount = 3,
  limit = 8,
): RecurringMerchant[] {
  const groups = new Map<string, { name: string; dates: number[]; totalSen: number }>();

  for (const transaction of transactions) {
    if (spendingDeltaSen(transaction, categories) <= 0) continue;
    const name = transaction.merchantNormalized || transaction.merchantOriginal;
    if (!name) continue;
    const key = name.toLowerCase();
    const entry = groups.get(key) ?? { name, dates: [], totalSen: 0 };
    entry.dates.push(Date.parse(transaction.occurredAt));
    entry.totalSen += transaction.amountSen;
    groups.set(key, entry);
  }

  return [...groups.values()]
    .filter((entry) => entry.dates.length >= minCount)
    .map((entry) => {
      const sorted = [...entry.dates].sort((a, b) => a - b);
      const gaps: number[] = [];
      for (let index = 1; index < sorted.length; index += 1) {
        gaps.push(((sorted[index] as number) - (sorted[index - 1] as number)) / 86_400_000);
      }
      gaps.sort((a, b) => a - b);
      const middle = Math.floor(gaps.length / 2);
      return {
        merchant: entry.name,
        count: entry.dates.length,
        totalSen: entry.totalSen,
        averageSen: Math.trunc(entry.totalSen / entry.dates.length),
        lastSeenAt: new Date(sorted[sorted.length - 1] as number).toISOString(),
        medianGapDays: gaps.length ? Math.round(gaps[middle] as number) : null,
      };
    })
    .sort((a, b) => b.count - a.count || b.totalSen - a.totalSen)
    .slice(0, limit);
}

export interface MonthComparison {
  currentSen: number;
  previousSen: number;
  differenceSen: number;
  /** Null when the previous month had no spending, so no divide-by-zero percentage. */
  percentChange: number | null;
  direction: 'up' | 'down' | 'same';
}

export function compareToPreviousMonth(
  transactions: readonly Transaction[],
  month: string,
  options: { timeZone?: string; categories?: readonly Category[] } = {},
): MonthComparison {
  const { timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  const currentSen = totalSpentSen(inRange(transactions, monthRange(month, timeZone)), categories);
  const previousSen = totalSpentSen(
    inRange(transactions, monthRange(addMonths(month, -1), timeZone)),
    categories,
  );
  const differenceSen = currentSen - previousSen;
  return {
    currentSen,
    previousSen,
    differenceSen,
    percentChange:
      previousSen > 0 ? Math.round((differenceSen / previousSen) * 1000) / 10 : null,
    direction: differenceSen > 0 ? 'up' : differenceSen < 0 ? 'down' : 'same',
  };
}

export interface MonthGroup {
  monthKey: string;
  transactions: Transaction[];
  totalSen: number;
}

/** Group a list for the Transactions screen: newest month first, newest first inside. */
export function groupByMonth(
  transactions: readonly Transaction[],
  options: { timeZone?: string; categories?: readonly Category[] } = {},
): MonthGroup[] {
  const { timeZone = DEFAULT_TIME_ZONE, categories = [] } = options;
  const groups = new Map<string, Transaction[]>();
  for (const transaction of activeTransactions(transactions)) {
    const key = monthKey(transaction.occurredAt, timeZone);
    const list = groups.get(key) ?? [];
    list.push(transaction);
    groups.set(key, list);
  }
  return [...groups.entries()]
    .map(([key, list]) => ({
      monthKey: key,
      transactions: list.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
      totalSen: totalSpentSen(list, categories),
    }))
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
}
